import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RotateCcw, ChevronDown, ChevronUp } from 'lucide-react'
import type { PrintSettings, PageInfo } from './constants'

// ─── Sub-components ─────────────────────────────────────────────────

function SettingsSection({ title, children, defaultOpen = true }: {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
      >
        {title}
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && <div className="px-4 pb-3 space-y-3">{children}</div>}
    </div>
  )
}

function SelectField({ label, value, options, onChange }: {
  label: string
  value: string | number
  options: { value: string | number; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <label className="block">
      <span className="text-xs text-gray-600">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full mt-0.5 rounded-xs border-gray-300 text-sm shadow-2xs focus:border-primary-500 focus:ring-primary-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  )
}

function ToggleField({ label, checked, onChange }: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex justify-between items-center">
      <span className="text-xs text-gray-600">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
          checked ? 'bg-primary-500' : 'bg-gray-300'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </label>
  )
}

function InputField({ label, value, onChange, placeholder }: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <label className="block">
      <span className="text-xs text-gray-600">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="block w-full mt-0.5 rounded-xs border-gray-300 text-sm shadow-2xs focus:border-primary-500 focus:ring-primary-500"
      />
    </label>
  )
}

/* NumberInputField — 현재 미사용, 추후 제본여백 설정 활성화 시 사용
function NumberInputField({ label, value, onChange, min, max, suffix }: {
  label: string
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
  suffix?: string
}) {
  return (
    <label className="block">
      <span className="text-xs text-gray-600">{label}</span>
      <div className="flex items-center gap-1 mt-0.5">
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          onChange={(e) => {
            const n = Number(e.target.value)
            if (!isNaN(n)) onChange(Math.max(min ?? 0, Math.min(max ?? 9999, n)))
          }}
          className="block w-full rounded-xs border-gray-300 text-sm shadow-2xs focus:border-primary-500 focus:ring-primary-500"
        />
        {suffix && <span className="text-xs text-gray-500 shrink-0">{suffix}</span>}
      </div>
    </label>
  )
}
*/

// ─── Main Panel ─────────────────────────────────────────────────────

export function PrintSettingsPanel({ settings, onChange, onPageInfoChange, onReset }: {
  settings: PrintSettings
  onChange: <K extends keyof PrintSettings>(key: K, value: PrintSettings[K]) => void
  onPageInfoChange: <K extends keyof PageInfo>(key: K, value: PageInfo[K]) => void
  onReset: () => void
}) {
  const { t } = useTranslation('admin')
  return (
    <div className="overflow-hidden w-64 bg-white rounded-lg border border-gray-200 shrink-0">
      <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">{t('print.settings')}</h3>
        <button
          onClick={onReset}
          className="text-gray-400 transition-colors hover:text-gray-600"
          title={t('print.resetDefaults')}
        >
          <RotateCcw size={14} />
        </button>
      </div>

      {/* 용지 설정 — 현재 미사용, 추후 활성화 예정
      <SettingsSection title={t('print.paper.title')}>
        <SelectField
          label={t('print.paper.size')}
          value={settings.paperSize}
          options={[
            { value: 'A4', label: 'A4 (210 x 297mm)' },
            { value: 'Letter', label: 'Letter (216 x 279mm)' },
          ]}
          onChange={(v) => onChange('paperSize', v as PaperSize)}
        />
        <SelectField
          label={t('print.paper.orientation')}
          value={settings.orientation}
          options={[
            { value: 'portrait', label: t('print.paper.portrait') },
            { value: 'landscape', label: t('print.paper.landscape') },
          ]}
          onChange={(v) => onChange('orientation', v as Orientation)}
        />
      </SettingsSection>
      */}

      <SettingsSection title={t('print.text.title')}>
        <SelectField
          label={t('print.text.columns')}
          value={settings.columns}
          options={[
            { value: 1, label: t('print.text.oneColumn') },
            { value: 2, label: t('print.text.twoColumns') },
          ]}
          onChange={(v) => onChange('columns', Number(v) as 1 | 2)}
        />
        <ToggleField
          label="Compact 레이아웃"
          checked={settings.compact}
          onChange={(v) => onChange('compact', v)}
        />
      </SettingsSection>

      {/* 헤더/푸터, 제본여백 — 현재 미사용, 추후 활성화 예정
      <SettingsSection title={t('print.header.title')}>
        <ToggleField
          label={t('print.header.showPageNumber')}
          checked={settings.showPageNumber}
          onChange={(v) => onChange('showPageNumber', v)}
        />
      </SettingsSection>

      <SettingsSection title="제본 여백">
        <ToggleField
          label="제본 여백 사용"
          checked={settings.gutterEnabled}
          onChange={(v) => onChange('gutterEnabled', v)}
        />
        {settings.gutterEnabled && (
          <NumberInputField
            label="여백 크기 (mm)"
            value={settings.gutterSize}
            onChange={(v) => onChange('gutterSize', v)}
            min={5}
            max={30}
            suffix="mm"
          />
        )}
      </SettingsSection>
      */}

      <SettingsSection title={t('print.bleed.title')}>
        <ToggleField
          label={t('print.bleed.enabled')}
          checked={settings.bleedEnabled}
          onChange={(v) => onChange('bleedEnabled', v)}
        />
      </SettingsSection>

      <SettingsSection title="페이지 정보" defaultOpen={false}>
        <InputField
          label="챕터 번호"
          value={settings.pageInfo.chapterNumber}
          onChange={(v) => onPageInfoChange('chapterNumber', v)}
          placeholder="07"
        />
        <InputField
          label="학년"
          value={settings.pageInfo.grade}
          onChange={(v) => onPageInfoChange('grade', v)}
          placeholder="4-1"
        />
        <div className="flex gap-2">
          <InputField
            label="단원 라벨"
            value={settings.pageInfo.unitLabel}
            onChange={(v) => onPageInfoChange('unitLabel', v)}
            placeholder="1단원."
          />
          <InputField
            label="단원명"
            value={settings.pageInfo.unitName}
            onChange={(v) => onPageInfoChange('unitName', v)}
            placeholder="큰 수"
          />
        </div>
        <InputField
          label="깨처 제목"
          value={settings.pageInfo.quetureTitle}
          onChange={(v) => onPageInfoChange('quetureTitle', v)}
          placeholder="나누기는 압축"
        />
        <InputField
          label="교재명"
          value={settings.pageInfo.bookLabel}
          onChange={(v) => onPageInfoChange('bookLabel', v)}
          placeholder="PRIME-A"
        />
      </SettingsSection>
    </div>
  )
}
